/**
 * Feature 4 (2026-05-28): Agreement Tasks API client.
 */
import { api } from './client';
import type {
  AgreementTask, AgreementTaskCreateInput, AgreementTaskUpdateInput,
  AgreementTaskStatus, Page, UUID,
} from './types';

export interface AgreementTaskMyCounts {
  open_count: number;
  overdue_count: number;
  scope: 'mine' | 'all';
}

export interface AgreementTasksListFilters {
  agreement_id?: UUID;
  /** #63: only "General" tasks (agreement_id IS NULL). */
  general?: boolean;
  statuses?: AgreementTaskStatus[];
  mine?: boolean;
  include_deleted?: boolean;
  // #47 (2026-06-12): per-column predicate filters (C-132).
  q?: string;                  // substring on description
  /** Multi-select since C-132 Amendment 3 (repeated query keys). */
  assignee_user_id?: UUID[];
  /** Created-by filter (Pedro 2026-06-12). Multi via repeated keys. */
  created_by_user_id?: UUID[];
  deadline_from?: string;      // YYYY-MM-DD inclusive
  deadline_to?: string;        // YYYY-MM-DD inclusive
  agreement_q?: string;        // parent Agreement code_1 / mnemonic
  /** P14: urgency levels to keep (1 High, 2 Medium, 3 Low). Multi via repeated keys. */
  urgency?: number[];
  /** P42: Primary Named RM user ids. Multi via repeated keys. */
  rm_user_id?: UUID[];
  /** P41: header sort, "key" or "-key" (server-side, across pages). */
  sort?: string;
}

export const agreementTasksApi = {
  async list(
    filters?: AgreementTasksListFilters,
    limit = 50,
    offset = 0,
  ): Promise<Page<AgreementTask>> {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (filters?.agreement_id) params.append('agreement_id', filters.agreement_id);
    if (filters?.general) params.append('general', 'true');
    if (filters?.statuses) {
      for (const s of filters.statuses) params.append('statuses', s);
    }
    if (filters?.mine) params.append('mine', 'true');
    if (filters?.include_deleted) params.append('include_deleted', 'true');
    // #47: predicate params — type AND serialiser in the same edit (C-133).
    if (filters?.q) params.append('q', filters.q);
    if (filters?.assignee_user_id) for (const u of filters.assignee_user_id) params.append('assignee_user_id', u);
    if (filters?.created_by_user_id) for (const u of filters.created_by_user_id) params.append('created_by_user_id', u);
    if (filters?.deadline_from) params.append('deadline_from', filters.deadline_from);
    if (filters?.deadline_to) params.append('deadline_to', filters.deadline_to);
    if (filters?.agreement_q) params.append('agreement_q', filters.agreement_q);
    if (filters?.urgency) for (const u of filters.urgency) params.append('urgency', String(u));
    if (filters?.rm_user_id) for (const u of filters.rm_user_id) params.append('rm_user_id', u);
    if (filters?.sort) params.append('sort', filters.sort);
    return api.get(`/agreement-tasks?${params}`);
  },

  async get(id: UUID): Promise<AgreementTask> {
    return api.get(`/agreement-tasks/${id}`);
  },

  async myCounts(): Promise<AgreementTaskMyCounts> {
    return api.get('/agreement-tasks/my-counts');
  },

  async create(input: AgreementTaskCreateInput): Promise<AgreementTask> {
    return api.post('/agreement-tasks', input);
  },

  async update(id: UUID, input: AgreementTaskUpdateInput): Promise<AgreementTask> {
    return api.patch(`/agreement-tasks/${id}`, input);
  },

  async updateStatus(id: UUID, status: AgreementTaskStatus): Promise<AgreementTask> {
    return api.patch(`/agreement-tasks/${id}/status`, { status });
  },

  async delete(id: UUID): Promise<AgreementTask> {
    return api.delete(`/agreement-tasks/${id}`);
  },

  async restore(id: UUID): Promise<AgreementTask> {
    return api.post(`/agreement-tasks/${id}/restore`);
  },
};
